import { TypedEvent } from "@siemens/ix";
import { Observable } from "rxjs";

/**
 * Creates an RxJS Observable from a Siemens iX TypedEvent.
 *
 * This function converts a Siemens iX TypedEvent into an RxJS Observable, allowing
 * for reactive programming patterns with iX events. The Observable will emit values
 * whenever the TypedEvent fires.
 *
 * @param event - The Siemens iX TypedEvent to convert
 *
 * @returns An Observable that emits values from the TypedEvent
 *
 * @example
 * ```typescript
 * const myEvent = new TypedEvent<string>();
 * const myObservable = fromIxTypedEvent(myEvent);
 *
 * // Subscribe to the observable
 * const subscription = myObservable.subscribe(value => {
 *   console.log(value);
 * });
 *
 * // Clean up when done
 * subscription.unsubscribe();
 * ```
 *
 * @throws Will propagate any errors that occur during event listener attachment
 */
export function fromIxTypedEvent<T>(event: TypedEvent<T>): Observable<T> {
  return new Observable<T>((subscriber) => {
    try {
      const listener = (value: T): void => subscriber.next(value);
      event.on(listener);
      return () => event.off(listener);
    } catch (err: unknown) {
      subscriber.error(err);
    }
    return;
  });
}
